import { useEffect, useState } from "react";
import { Truck, Check, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDateTime, inr } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function Trucks() {
  const [bookings, setBookings] = useState(null);
  const [forms, setForms] = useState({});
  const [busy, setBusy] = useState("");

  const load = () =>
    appApi.get("admin/truck-bookings").then((d) => {
      const list = (d.bookings || []).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setBookings(list);
    }).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const setF = (id, k) => (e) => setForms((f) => ({ ...f, [id]: { ...(f[id] || {}), [k]: e.target.value } }));

  const allocate = async (b) => {
    const f = forms[b.id] || {};
    if (!f.driver_name || !f.driver_phone || !f.vehicle_number || !f.quoted_amount) {
      toast.error("Driver name, phone, vehicle number and quoted amount are all required.");
      return;
    }
    setBusy(b.id);
    try {
      await appApi.post(`admin/truck-bookings/${b.id}/allocate`, {
        driver_name: f.driver_name,
        driver_phone: f.driver_phone,
        vehicle_number: f.vehicle_number,
        quoted_amount: parseFloat(f.quoted_amount),
      });
      toast.success(`Truck allocated at ${inr(parseFloat(f.quoted_amount))} — ${b.merchant_name} notified.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const pendingCount = (bookings || []).filter((b) => b.status === "pending_allocation").length;

  const markStatus = async (b, status) => {
    const notes = window.prompt(`Note for ${b.reference_no} → ${status.replace(/_/g, " ")} (optional):`, "") ?? "";
    setBusy(b.id + status);
    try {
      await appApi.post(`admin/truck-bookings/${b.id}/status`, { status, notes });
      toast.success(`${b.reference_no} marked ${status.replace(/_/g, " ")} — merchant notified.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="trucks-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
          <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Truck Bookings</h1>
          <p className="text-sm text-zinc-500 mt-2">Newest first — quote your rate, allocate a driver, merchant gets notified.</p>
        </div>
        {bookings && (
          <span className="font-mono text-xs font-bold uppercase tracking-widest bg-obsidian text-sky-300 px-4 py-2" data-testid="trucks-pending-count">
            {pendingCount} pending allocation
          </span>
        )}
      </div>

      <div className="space-y-4" data-testid="trucks-list">
        {(bookings || []).map((b) => {
          const al = b.allocation || {};
          const allocated = b.status !== "pending_allocation";
          return (
            <div key={b.id} className={`border-2 border-obsidian bg-white p-6 ${b.status === "pending_allocation" ? "shadow-[6px_6px_0px_0px_#2563EB]" : ""}`} data-testid={`truck-card-${b.reference_no}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><Truck className="w-5 h-5" /></span>
                  <div>
                    <p className="font-bold font-mono text-sm">{b.reference_no}</p>
                    <p className="text-xs text-zinc-500">{b.merchant_name} · {b.merchant_business} · {b.merchant_email}</p>
                    <p className="text-xs font-mono text-zinc-400 mt-0.5">
                      {b.vehicle?.name || b.vehicle_id} · {b.weight_kg}kg · {b.pieces} pc · {b.goods_description} · pickup {b.pickup_date}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <StatusBadge status={b.status || "pending_allocation"} />
                  {b.quoted_amount != null && (
                    <p className="font-display font-black text-xl tracking-tight mt-1.5 flex items-center gap-1 justify-end" data-testid={`truck-quote-${b.id}`}>
                      <IndianRupee className="w-4 h-4" />{Number(b.quoted_amount).toLocaleString("en-IN")}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
                <div className="border border-zinc-200 p-4">
                  <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Pickup</p>
                  <p className="font-semibold mt-1">{b.pickup?.contact_name} · {b.pickup?.phone}</p>
                  <p className="text-xs text-zinc-500">{b.pickup?.address}, {b.pickup?.city}, {b.pickup?.state} — {b.pickup?.pincode}</p>
                </div>
                <div className="border border-zinc-200 p-4">
                  <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-zinc-400">Delivery</p>
                  <p className="font-semibold mt-1">{b.delivery?.contact_name} · {b.delivery?.phone}</p>
                  <p className="text-xs text-zinc-500">{b.delivery?.address}, {b.delivery?.city}, {b.delivery?.state} — {b.delivery?.pincode}</p>
                </div>
              </div>

              <p className="text-[10px] font-mono text-zinc-400 mt-3 uppercase tracking-widest">Booked {fmtDateTime(b.created_at)}</p>

              {allocated && al.driver_name && (
                <div className="mt-4 border-2 border-obsidian bg-paper px-4 py-3 text-xs font-mono" data-testid={`truck-allocation-${b.id}`}>
                  Driver <span className="font-bold">{al.driver_name}</span> · {al.driver_phone} · Vehicle <span className="font-bold">{al.vehicle_number}</span>
                  {al.allocated_at && <span className="text-zinc-400"> · allocated {fmtDateTime(al.allocated_at)}</span>}
                </div>
              )}

              {allocated && b.status !== "delivered" && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {b.status !== "in_transit" && (
                    <button onClick={() => markStatus(b, "in_transit")} disabled={busy === b.id + "in_transit"} data-testid={`truck-transit-${b.id}`}
                      className="inline-flex items-center gap-1.5 bg-white text-obsidian text-xs font-bold uppercase tracking-widest px-5 py-2.5 border-2 border-obsidian hover:bg-acid hover:text-white transition-colors duration-150 disabled:opacity-60">
                      <Truck className="w-3.5 h-3.5" /> {busy === b.id + "in_transit" ? "Updating…" : "Mark In Transit"}
                    </button>
                  )}
                  <button onClick={() => markStatus(b, "delivered")} disabled={busy === b.id + "delivered"} data-testid={`truck-delivered-${b.id}`}
                    className="inline-flex items-center gap-1.5 bg-acid text-white text-xs font-bold uppercase tracking-widest px-5 py-2.5 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-60">
                    <Check className="w-3.5 h-3.5" /> {busy === b.id + "delivered" ? "Updating…" : "Mark Delivered"}
                  </button>
                </div>
              )}

              {!allocated && (
                <div className="flex flex-wrap gap-3 mt-4 items-end">
                  {[
                    { k: "driver_name", ph: "Driver name" },
                    { k: "driver_phone", ph: "Driver phone" },
                    { k: "vehicle_number", ph: "Vehicle number" },
                    { k: "quoted_amount", ph: "Your quoted rate ₹", type: "number" },
                  ].map((f) => (
                    <input key={f.k} type={f.type || "text"} min={f.type === "number" ? "1" : undefined}
                      value={(forms[b.id] || {})[f.k] || ""} onChange={setF(b.id, f.k)} placeholder={f.ph}
                      data-testid={`truck-${f.k}-${b.id}`}
                      className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian w-44" />
                  ))}
                  <button onClick={() => allocate(b)} disabled={busy === b.id} data-testid={`truck-allocate-${b.id}`}
                    className="inline-flex items-center gap-1.5 bg-acid text-white text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-60">
                    <Check className="w-3.5 h-3.5" /> {busy === b.id ? "Allocating…" : "Allocate & Notify"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {bookings && bookings.length === 0 && (
          <div className="border-2 border-obsidian bg-white p-10 text-sm text-zinc-500" data-testid="trucks-empty">No truck bookings yet.</div>
        )}
        {!bookings && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading bookings…</p>}
      </div>
    </div>
  );
}
